import type React from 'react';
import { useEffect } from 'react';
import { matchPath } from 'react-router-dom';
import { Anchor, Box, Button, Heading, Paragraph } from '@zillow/constellation-10';
import Page from '@common/components/Page';
import PaymentsIcon from '@assets/rent-payments.svg';
import { PROPERTY_STATUS } from '@rental-manager/constants/OnboardingConstants';
import userPersonas from '@common/constants/userPersonas';
import StringConstants from '@common/constants/StringConstants';
import REQUEST_STATUS from '@common/constants/requestStatus';
import ResponseMessageCode from '@common/constants/ResponseMessageCode';
import DTWrenchIcon from '@assets/dt-icon-wrench.svg'; // TODO: replace with constellation lib
import Modal from '@common/components/ConstellationV10Modal';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  baseUrl: string;
  propertyStatus: string;
  showSetupNofication: () => void;
  onNext: (collectMoveInCosts: boolean) => void;
  collectMoveInCosts: boolean;
  getLandlordStatusRequestStatus: string;
  getLandlordStatusMessageCode: string;
  currentProperty?: ZrmClickstreamListing;
  user?: ZrmClickstreamUser;
}

const OnboardingLandingPage: React.FC<Props> = ({
  baseUrl,
  propertyStatus = PROPERTY_STATUS.NOT_STARTED,
  showSetupNofication,
  onNext,
  collectMoveInCosts = false,
  getLandlordStatusRequestStatus,
  getLandlordStatusMessageCode,
  currentProperty,
  user,
}) => {
  useEffect(() => {
    const isBaseUrl = matchPath(window.location.pathname, {
      path: baseUrl,
      exact: true,
      strict: false,
    });
    if (
      propertyStatus !== PROPERTY_STATUS.NOT_STARTED &&
      propertyStatus !== PROPERTY_STATUS.TENANT_INFO_SET &&
      isBaseUrl
    ) {
      showSetupNofication();
    }
  }, [baseUrl, propertyStatus, showSetupNofication]);

  const gotoNextPage = (): void => {
    if (currentProperty && user) {
      IreneOnboardingCSEvents.emitStartCollectingPaymentsEvent({ currentProperty, user });
    }
    onNext(collectMoveInCosts);
  };

  const trackLearnMoreClick = (): void => {
    if (currentProperty && user) {
      IreneOnboardingCSEvents.emitLearnMoreLinkEvent({ currentProperty, user });
    }
  };

  const renderCTA = (startButtonCopy: Record<string, string>): React.ReactNode => {
    return (
      <Box>
        <Button emphasis="filled" fluid size="md" onClick={gotoNextPage}>
          {collectMoveInCosts ? startButtonCopy.mic : startButtonCopy.default}
        </Button>
      </Box>
    );
  };

  const renderStartButtonLegalCopy = (startButtonCopy: Record<string, string>): string => {
    return Object.values(startButtonCopy)
      .map((copy) => `"${copy}"`)
      .join(' or ');
  };

  const shouldShowLeaseRetrievalFailedModal =
    getLandlordStatusRequestStatus === REQUEST_STATUS.FAIL &&
    getLandlordStatusMessageCode === ResponseMessageCode.LEASE_RETRIEVAL_FAILED;

  const utmCampaign = 'utm_campaign=payment_tab_learn_more';
  const marketingLink = `${StringConstants.LINKS.MARKETING_PAGE.ZRM_PAYMENTS}?${utmCampaign}`;
  const startButtonCopy = { default: 'Start collecting payments', mic: 'Collect move-in costs' };
  return (
    <Page persona={userPersonas.LANDLORD} dataAutoTestId="landlord-onboard-landing-page">
      <Box
        css={{
          margin: 'token(spacing.xl) auto !important',
          display: 'grid',
          textAlign: 'center',
          gridTemplateRows: 'auto',
          gap: 'token(spacing.md) !important',
          maxWidth: { lg: '60%', md: '80%', sm: '100%' },
        }}
      >
        <Box>
          <img src={PaymentsIcon as unknown as string} alt="PaymentIcon" />
          <Box css={{ marginTop: 'token(spacing.md) !important' }}>
            <Heading level={4}>Say hello to online payments</Heading>
          </Box>
        </Box>

        <Box>
          <Paragraph color="text.subtle">
            You get a free, secure way to collect rent and move-in costs right into your bank account. Your tenant gets
            a free way to pay and more flexibility. You both get freedom from checks.
            <Anchor
              css={{ textDecoration: 'none', display: 'block', padding: 'token(spacing.xs)' }}
              onClick={trackLearnMoreClick}
              href={marketingLink}
              target="_blank"
            >
              Learn more
            </Anchor>
          </Paragraph>
        </Box>
        <Box>{renderCTA(startButtonCopy)}</Box>
        <Box>
          <Paragraph color="text.subtle" textStyle="fineprint">
            By clicking {renderStartButtonLegalCopy(startButtonCopy)} you agree to comply with the&nbsp;
            <Anchor css={{ textDecoration: 'none' }} target="_blank" href="https://www.zillow.com/corp/Terms.htm">
              Terms of Use
            </Anchor>
            ,&nbsp;
            <Anchor
              css={{ textDecoration: 'none' }}
              target="_blank"
              href="https://www.zillow.com/renter-hub/terms/Rental-User-Terms"
            >
              Rental User Terms
            </Anchor>
            , and our third-party payment processor&apos;s
            <Anchor css={{ textDecoration: 'none' }} target="_blank" href="https://stripe.com/us/connect-account/legal">
              &nbsp;Payment Terms
            </Anchor>
            &nbsp;and
            <Anchor css={{ textDecoration: 'none' }} target="_blank" href="https://plaid.com/legal">
              &nbsp;Bank Authorization Terms.
            </Anchor>
          </Paragraph>
        </Box>
      </Box>
      {shouldShowLeaseRetrievalFailedModal && (
        <Modal
          persona={userPersonas.LANDLORD}
          header={
            <Box css={{ padding: 'token(spacing.sm) !important', textAlign: 'center' }}>
              <Heading level={2} textStyle="heading-xs">
                A problem occurred
              </Heading>
            </Box>
          }
          body={
            <Box
              css={{
                padding: 'token(spacing.md) !important',
                textAlign: 'center',
                display: 'grid',
                gap: 'token(spacing.xs)',
              }}
            >
              <Box>
                <img src={DTWrenchIcon as unknown as string} className="centeredImg" alt="Wrench Icon" height="44" />
              </Box>
              <Box>
                <Paragraph textStyle="body-bold">There&apos;s been an error </Paragraph>
              </Box>
              <Box>
                <Paragraph>
                  We aren&apos;t currently able to access your leases, which is required for setting up payments. Wait a
                  few minutes and try again.
                </Paragraph>
              </Box>
            </Box>
          }
          footer={
            <Box css={{ padding: 'token(spacing.sm) !important' }}>
              <DebouncedButton onClick={() => window.location.reload()}>Try again</DebouncedButton>
            </Box>
          }
        />
      )}
    </Page>
  );
};

export default OnboardingLandingPage;
